import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useNavigate } from 'react-router-dom';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California',
  'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida',
  'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana',
  'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
].sort();

const OCCUPATIONS = [
  '변호사', '법학전문대학원생', '법학전문대학원 졸업생', '사법연수원생',
  '사법연수원 수료생', '공무원', '기업체 임직원', '교수/강사', '학생', '기타'
].sort((a, b) => a === '기타' ? 1 : b === '기타' ? -1 : a.localeCompare(b));

const ticketTypeLabels = {
    'MEE_AI_6': 'MEE 응시권 (AI 평가, 6문제)',
    'MEE_PROFESSOR_6': 'MEE 응시권 (Professor 평가, 6문제)',
    'MPT_AI_2': 'MPT 응시권 (AI 평가, 2문제)',
    'MPT_PROFESSOR_2': 'MPT 응시권 (Professor 평가, 2문제)',
    'STUDY_MEE_1': '셀프 MEE 스터디권 (1문제)',
    'STUDY_MPT_1': '셀프 MPT 스터디권 (1문제)',
};

const ProfileSettings = () => {
  const { user, userProfile, updateProfile, isLoading: authLoading, isEvaluator } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  
  const [yangBalance, setYangBalance] = useState(0);
  const [subscriptionExpiry, setSubscriptionExpiry] = useState(null);
  const [examTickets, setExamTickets] = useState([]);
  const [studyTickets, setStudyTickets] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);

  useEffect(() => {
    if (userProfile) {
      const occupation = userProfile.current_occupation && OCCUPATIONS.includes(userProfile.current_occupation) 
        ? userProfile.current_occupation 
        : '기타';
      
      const initialFormData = {
        name: userProfile.name || '',
        bar_exam_state: userProfile.bar_exam_state || '',
        bar_exam_year: userProfile.bar_exam_year || '',
        bar_exam_month: userProfile.bar_exam_month || '',
        current_occupation: occupation,
        custom_occupation: occupation === '기타' ? userProfile.current_occupation : '',
        birth_year: userProfile.birth_year || '',
        gender: userProfile.gender || '',
        law_school_name: userProfile.law_school_name || '',
        graduation_year: userProfile.graduation_year || '',
      };
      setFormData(initialFormData);
      
      setYangBalance(userProfile.yang_balance || 0);
      setSubscriptionExpiry(userProfile.subscription_expiry);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!user) return;
    
    // Fetch tickets and balances
    const fetchPurchaseInfo = async () => {
      const { data: examData } = await supabase.from('user_exam_tickets').select('type, count').eq('user_id', user.id);
      setExamTickets(examData || []);
      
      const { data: studyData } = await supabase.from('user_study_tickets').select('type, count').eq('user_id', user.id);
      setStudyTickets(studyData || []);

      const { data: historyData } = await supabase
        .from('yang_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'purchase')
        .order('created_at', { ascending: false })
        .limit(10);
      setPurchaseHistory(historyData || []);
    };
    
    fetchPurchaseInfo();
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'current_occupation' && value !== '기타' && { custom_occupation: '' })
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const submitData = {
        ...formData,
        current_occupation: formData.current_occupation === '기타' 
          ? formData.custom_occupation 
          : formData.current_occupation
      };
      delete submitData.custom_occupation;

      await updateProfile(submitData);
      setMessage('프로필이 성공적으로 업데이트되었습니다.');
      setIsEditMode(false);
    } catch (error) {
      console.error('프로필 업데이트 오류:', error);
      setError(error.message || '프로필 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form data to original profile data
    if (userProfile) {
        const occupation = userProfile.current_occupation && OCCUPATIONS.includes(userProfile.current_occupation) ? userProfile.current_occupation : '기타';
        setFormData({
            name: userProfile.name || '',
            bar_exam_state: userProfile.bar_exam_state || '',
            bar_exam_year: userProfile.bar_exam_year || '',
            bar_exam_month: userProfile.bar_exam_month || '',
            current_occupation: occupation,
            custom_occupation: occupation === '기타' ? userProfile.current_occupation : '',
            birth_year: userProfile.birth_year || '',
            gender: userProfile.gender || '',
            law_school_name: userProfile.law_school_name || '',
            graduation_year: userProfile.graduation_year || '',
        });
    }
  };

  const handlePasswordReset = async () => {
    try {
      await supabase.auth.resetPasswordForEmail(user.email, {
          redirectTo: `${window.location.origin}/change-password`,
      });
      setMessage('비밀번호 재설정 링크가 이메일로 전송되었습니다.');
    } catch (error) {
      setError('비밀번호 재설정 이메일 전송에 실패했습니다.');
    }
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!userProfile) {
    return (
      <Box sx={{ p: 3, maxWidth: '1000px', margin: '0 auto' }}>
        <Typography variant="h4" gutterBottom>
          프로필을 찾을 수 없습니다.
        </Typography>
        <Alert severity="warning">
          프로필 정보를 불러오는 데 실패했습니다. 잠시 후 다시 시도해 주세요.
        </Alert>
      </Box>
    );
  }
  
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear + i);
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const graduationYearOptions = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  
  const renderInfoCard = (icon, title, content) => (
    <Grid item xs={12}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: '12px',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          {icon}
          <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ ml: 5 }}>
          {content}
        </Typography>
      </Paper>
    </Grid>
  );

  const renderDisplayView = () => (
    <>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{userProfile.name || '사용자'}님 프로필</Typography>
        <IconButton 
          onClick={() => setIsEditMode(true)}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
            color: 'white'
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {renderInfoCard(
          <GavelIcon color="primary" />,
          "Bar Exam 응시 정보",
          <>
            {userProfile.bar_exam_state || '미설정'} / {userProfile.bar_exam_year && userProfile.bar_exam_month ? `${userProfile.bar_exam_year}년 ${userProfile.bar_exam_month}월` : '미설정'}
          </>
        )}
        {renderInfoCard(
          <PersonIcon color="primary" />,
          "개인 정보",
          <>
            {userProfile.current_occupation || '미설정'} / {userProfile.birth_year ? `${userProfile.birth_year}년생` : '미설정'} / {userProfile.gender === 'male' ? '남성' : userProfile.gender === 'female' ? '여성' : '미선택'}
          </>
        )}
        {renderInfoCard(
          <SchoolIcon color="primary" />,
          "로스쿨 정보",
          <>
            {userProfile.law_school_name || '미설정'} {userProfile.graduation_year && `(${userProfile.graduation_year}년 졸업${parseInt(userProfile.graduation_year) >= new Date().getFullYear() ? '예정' : ''})`}
          </>
        )}
        {isEvaluator && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '12px',
                bgcolor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <VpnKeyIcon color="success" />
                <Typography variant="h6" color="success.dark" sx={{ fontWeight: 500 }}>
                  평가자 도구
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={2} mt={2} sx={{ ml: 5 }}>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/evaluator')}
                  color="success"
                >
                  평가자 페이지로 이동
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.secondary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <VpnKeyIcon color="secondary" />
              <Typography variant="h6" color="secondary" sx={{ fontWeight: 500 }}>
                계정 관리
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={2} mt={2} sx={{ ml: 5 }}>
                <Button variant="outlined" onClick={handlePasswordReset}>
                    비밀번호 변경
                </Button>
                <Button variant="outlined" color="error">
                    회원 탈퇴 (구현 예정)
                </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  const renderPurchaseTable = () => (
    <Box mt={6}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>구매/보유 내역</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>구분</TableCell>
              <TableCell>상품명/권종</TableCell>
              <TableCell align="right">수량/상태</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>잔액</TableCell>
              <TableCell>냥(🐾)</TableCell>
              <TableCell align="right">{yangBalance} 🐾</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>정기권</TableCell>
              <TableCell>학습권/정기권</TableCell>
              <TableCell align="right">{subscriptionExpiry && new Date(subscriptionExpiry) > new Date() ? `~ ${new Date(subscriptionExpiry).toLocaleDateString()}` : '만료'}</TableCell>
            </TableRow>
            {examTickets.map((t) => (
              <TableRow key={`exam-${t.type}`}>
                <TableCell>응시권</TableCell>
                <TableCell>{ticketTypeLabels[t.type] || t.type}</TableCell>
                <TableCell align="right">{t.count}개</TableCell>
              </TableRow>
            ))}
            {studyTickets.map((t) => (
              <TableRow key={`study-${t.type}`}>
                <TableCell>스터디권</TableCell>
                <TableCell>{ticketTypeLabels[t.type] || t.type}</TableCell>
                <TableCell align="right">{t.count}개</TableCell>
              </TableRow>
            ))}
            {purchaseHistory.length > 0 && (
              <TableRow>
                <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.grey[500], 0.1) }}>
                  최근 구매 내역
                </TableCell>
              </TableRow>
            )}
            {purchaseHistory.map((tx) => (
              <TableRow key={`purchase-${tx.id}`}>
                <TableCell>{tx.description || tx.metadata?.product_id}</TableCell>
                <TableCell>{new Date(tx.created_at).toLocaleString('ko-KR')}</TableCell>
                <TableCell align="right">{-tx.amount}냥</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderEditForm = () => (
    <form onSubmit={handleSubmit}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>프로필 수정</Typography>
        <Box>
          <IconButton onClick={handleCancel} sx={{ mr: 1, color: 'white' }}><CancelIcon /></IconButton>
          <Button type="submit" variant="contained" disabled={loading} startIcon={<SaveIcon />} sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f5f5f5' } }}>
            {loading ? '저장 중...' : '저장'}
          </Button>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {/* Form fields will go here, similar to your provided code */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Bar Exam 정보</Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>응시 주</InputLabel>
            <Select name="bar_exam_state" value={formData.bar_exam_state || ''} onChange={handleChange} label="응시 주">
              {US_STATES.map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>응시 년도</InputLabel>
                <Select name="bar_exam_year" value={formData.bar_exam_year || ''} onChange={handleChange} label="응시 년도">
                  {yearOptions.map(year => <MenuItem key={year} value={year}>{year}년</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>응시 월</InputLabel>
                <Select name="bar_exam_month" value={formData.bar_exam_month || ''} onChange={handleChange} label="응시 월">
                  {monthOptions.map(month => <MenuItem key={month} value={month}>{month}월</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}><Divider sx={{my: 2}} /></Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>개인 정보</Typography>
          <TextField fullWidth label="이름" name="name" value={formData.name || ''} onChange={handleChange} margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel>현재 직업</InputLabel>
            <Select name="current_occupation" value={formData.current_occupation || ''} onChange={handleChange} label="현재 직업">
              {OCCUPATIONS.map(occ => <MenuItem key={occ} value={occ}>{occ}</MenuItem>)}
            </Select>
          </FormControl>
          {formData.current_occupation === '기타' && (
            <TextField fullWidth label="직업 직접 입력" name="custom_occupation" value={formData.custom_occupation || ''} onChange={handleChange} margin="normal" />
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="출생년도" name="birth_year" type="number" value={formData.birth_year || ''} onChange={handleChange} margin="normal" />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>성별</InputLabel>
                <Select name="gender" value={formData.gender || ''} onChange={handleChange} label="성별">
                  <MenuItem value="male">남성</MenuItem>
                  <MenuItem value="female">여성</MenuItem>
                  <MenuItem value="other">기타</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}><Divider sx={{my: 2}} /></Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>로스쿨 정보</Typography>
          <TextField fullWidth label="로스쿨 이름" name="law_school_name" value={formData.law_school_name || ''} onChange={handleChange} margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel>졸업(예정) 년도</InputLabel>
            <Select name="graduation_year" value={formData.graduation_year || ''} onChange={handleChange} label="졸업(예정) 년도">
              {graduationYearOptions.map(year => <MenuItem key={year} value={year}>{year}년</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );

  return (
    <Box p={{ xs: 1, sm: 2, md: 4 }} sx={{ maxWidth: '1000px', margin: '0 auto' }}>
      <Paper 
        elevation={3}
        sx={{ 
          borderRadius: '20px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          p: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2, }} onClose={() => setMessage(null)}>{message}</Alert>}
        
        {isEditMode ? renderEditForm() : (
          <>
            {renderDisplayView()}
            {renderPurchaseTable()}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProfileSettings; 